import {
	type MicrositeAddress,
	MicrositeColor,
	type MicrositeConfig,
	type MicrositeFontFamily,
	type MicrositeListingData,
	type MicrositeProfile,
	type MicrositeResponsiveImage,
	type MicrositeTheme,
	type MicrositeVideoData,
	SmoothScrollingMode,
} from '~/model';

export const DEMO_FONT_MONTSERRAT: MicrositeFontFamily = {
	stack: 'Montserrat, Helvetica, Arial, "Open Sans", sans-serif',
	url: 'https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap',
};

const DEMO_GAP = '1.5rem';
const DEMO_WIDTH = '1200px';

export const DEMO_THEME: MicrositeTheme = {
	viewport: {
		small: 480,
		medium: 768,
		large: 1024,
		xlarge: 1280,
	},
	font: {
		family: DEMO_FONT_MONTSERRAT,
		spacing: {
			lg: '0.45em',
			md: '0.25em',
			sm: '0.15em',
		},
	},
	rail: {
		width: '22rem',
	},
	radius: '0.4rem',
	gap: {
		grid: '1.5rem',
		content: '1rem',
		large: '2rem',
		section: '4rem',
	},
	margin: {
		left: `max(${DEMO_GAP}, calc((100% - ${DEMO_WIDTH}) / 2))`,
		right: `max(${DEMO_GAP}, calc((100% - ${DEMO_WIDTH}) / 2))`,
	},
	color: {
		white: {
			fill: '#FFFFFF',
			text: {
				strong: '#FFFFFF',
				weak: '#FFFFFF',
			},
			stroke: {
				strong: '#FFFFFF',
				weak: '#FFFFFF',
			},
		},
		green: {
			fill: '#F5FAF8',
			text: {
				strong: '#067A55',
				weak: '#6AAF99',
			},
			stroke: {
				strong: '#8FC3B3',
				weak: '#E6F2EE',
			},
		},
		red: {
			fill: '#FBF5F5',
			text: {
				strong: '#A1132E',
				weak: '#CC6B7F',
			},
			stroke: {
				strong: '#D69BA8',
				weak: '#F3E4E8',
			},
		},
		black: {
			fill: '#F5F5F5',
			text: {
				strong: '#191919',
				weak: '#666666',
			},
			stroke: {
				strong: '#8C8C8C',
				weak: '#E6E6E6',
			},
		},
		brand: {
			fill: '#F5F5F5',
			text: {
				strong: '#191919',
				weak: '#666666',
			},
			stroke: {
				strong: '#8C8C8C',
				weak: '#E6E6E6',
			},
		},
		stroke: MicrositeColor.BLACK,
		background: MicrositeColor.WHITE,
		fill: MicrositeColor.BLACK,
	},
};

export const DEMO_FEATURES = [
	'Gated Community',
	'Beach Access',
	'Heated Pool',
	'High Ceilings',
	'Large Kitchen',
	'Large Lot',
	'Great Schools',
	'Open Floor Plan',
	'Ocean Views',
	'Shopping Nearby',
	'Walk-In Closets',
];

export const DEMO_CONFIG: MicrositeConfig = {
	smooth: SmoothScrollingMode.CSS,
	theme: DEMO_THEME,
};

export const DEMO_MAP =
	'https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/pin-l+000000(-121.9785,37.2942)/-121.9785,37.2942,10.97,0/1280x500@2x?attribution=false&logo=false&access_token=pk.eyJ1IjoicDJzb2Z0d2FyZSIsImEiOiJjbG5ncThucXgwNTBqMmxydm9kcHlyeHo5In0.SWy7DA1JMIEfloyvL41uOQ';

const DEMO_FLOORPLAN_URL_1 = `https://uploads.mls.tours/twt/order/e226cc8a-338a-4138-887a-02e3642e28e7/job/e5651485-2fc9-4ffc-87b5-74366f4cd37f/0f48e206de4c32e82eff943d8ff5dcd7/1st_floor_830_affazia_lane_georgetown_with_dim.svg`;
const DEMO_FLOORPLAN_URL_2 = `https://uploads.mls.tours/twt/order/e226cc8a-338a-4138-887a-02e3642e28e7/job/e5651485-2fc9-4ffc-87b5-74366f4cd37f/bc86981f45a6fde9eb97042e8e24bf10/2nd_floor_830_affazia_lane_georgetown_with_dim.svg`;
const HEADSHOT_1 = `https://uploads.mls.tours/twt/customer/18545b35-db74-4dd1-8ee3-e28c3477270a/headshot/7dbd6aafe7f6f175d90af89bca91d197/responsive`;

export const DEMO_PROFILES: MicrositeProfile[] = [
	{
		name: 'John Doe',
		title: 'Realtor',
		company: 'Acme Realty',
		phone: '555-123-4567',
		email: 'john@doe.com',
		license: '048978',
		website: 'https://www.johndoehomes.com',
		headshot: {
			medium_2x: `${HEADSHOT_1}/medium_2x.jpeg`,
			medium_1x: `${HEADSHOT_1}/medium_1x.jpeg`,
			small_2x: `${HEADSHOT_1}/small_2x.jpeg`,
			small_1x: `${HEADSHOT_1}/small_1x.jpeg`,
			small_3x: `${HEADSHOT_1}/small_3x.jpeg`,
			large_1x: `${HEADSHOT_1}/large_1x.jpeg`,
			large_2x: `${HEADSHOT_1}/large_2x.jpeg`,
		},
		logo: 'https://uploads.mls.tours/twt/customer/dcb806be-87d7-4eb8-8d2a-d1283f9f1b7b/profile/835c6438-0280-4cd0-8612-afc786546230/logo.svg',
	},
	{
		name: 'Jane Doe',
		title: 'Agent',
		company: 'Jane Realty',
		phone: '456-799-4321',
		license: '784569',
		email: 'jane@doe.com',
		website: 'https://www.janedoehomes.com',
		headshot: {
			medium_2x: `${HEADSHOT_1}/medium_2x.jpeg`,
			medium_1x: `${HEADSHOT_1}/medium_1x.jpeg`,
			small_2x: `${HEADSHOT_1}/small_2x.jpeg`,
			small_1x: `${HEADSHOT_1}/small_1x.jpeg`,
			small_3x: `${HEADSHOT_1}/small_3x.jpeg`,
			large_1x: `${HEADSHOT_1}/large_1x.jpeg`,
			large_2x: `${HEADSHOT_1}/large_2x.jpeg`,
		},
		logo: 'https://uploads.mls.tours/twt/customer/dcb806be-87d7-4eb8-8d2a-d1283f9f1b7b/profile/835c6438-0280-4cd0-8612-afc786546230/logo.svg',
	},
];

export const DEMO_FLOORPLANS: MicrositeResponsiveImage[] = [
	{
		large_1x: DEMO_FLOORPLAN_URL_1,
		large_2x: DEMO_FLOORPLAN_URL_1,
		medium_1x: DEMO_FLOORPLAN_URL_1,
		medium_2x: DEMO_FLOORPLAN_URL_1,
		small_1x: DEMO_FLOORPLAN_URL_1,
		small_2x: DEMO_FLOORPLAN_URL_1,
		small_3x: DEMO_FLOORPLAN_URL_1,
	},
	{
		large_1x: DEMO_FLOORPLAN_URL_2,
		large_2x: DEMO_FLOORPLAN_URL_2,
		medium_1x: DEMO_FLOORPLAN_URL_2,
		medium_2x: DEMO_FLOORPLAN_URL_2,
		small_1x: DEMO_FLOORPLAN_URL_2,
		small_2x: DEMO_FLOORPLAN_URL_2,
		small_3x: DEMO_FLOORPLAN_URL_2,
	},
];

const DEMO_SLIDESHOW_PREFIX_1 =
	'https://uploads.mls.tours/twt/order/e226cc8a-338a-4138-887a-02e3642e28e7/slideshow/bacfda5edfe5b6f17c0e767513482657';

export const DEMO_SLIDESHOW_1: MicrositeVideoData = {
	m3u8: `${DEMO_SLIDESHOW_PREFIX_1}/hls/playlist.m3u8`,
	mp4: `${DEMO_SLIDESHOW_PREFIX_1}/mp4/1080p.mp4`,
	thumbnail: {
		large_2x: `${DEMO_SLIDESHOW_PREFIX_1}/responsive/large_2x.jpeg`,
		large_1x: `${DEMO_SLIDESHOW_PREFIX_1}/responsive/large_1x.jpeg`,
		medium_2x: `${DEMO_SLIDESHOW_PREFIX_1}/responsive/medium_2x.jpeg`,
		medium_1x: `${DEMO_SLIDESHOW_PREFIX_1}/responsive/medium_1x.jpeg`,
		small_3x: `${DEMO_SLIDESHOW_PREFIX_1}/responsive/small_3x.jpeg`,
		small_2x: `${DEMO_SLIDESHOW_PREFIX_1}/responsive/small_2x.jpeg`,
		small_1x: `${DEMO_SLIDESHOW_PREFIX_1}/responsive/small_1x.jpeg`,
	},
};

const DEMO_SLIDESHOW_PREFIX_2 =
	'https://uploads.mls.tours/twt/order/a19ff8ed-272e-4548-bc33-26a897059133/slideshow/73e6889601a9962135dec0144770cdf7';

export const DEMO_SLIDESHOW_2: MicrositeVideoData = {
	m3u8: `${DEMO_SLIDESHOW_PREFIX_2}/hls/playlist.m3u8`,
	mp4: `${DEMO_SLIDESHOW_PREFIX_2}/mp4/1080p.mp4`,
	thumbnail: {
		large_2x: `${DEMO_SLIDESHOW_PREFIX_2}/responsive/large_2x.jpeg`,
		large_1x: `${DEMO_SLIDESHOW_PREFIX_2}/responsive/large_1x.jpeg`,
		medium_2x: `${DEMO_SLIDESHOW_PREFIX_2}/responsive/medium_2x.jpeg`,
		medium_1x: `${DEMO_SLIDESHOW_PREFIX_2}/responsive/medium_1x.jpeg`,
		small_3x: `${DEMO_SLIDESHOW_PREFIX_2}/responsive/small_3x.jpeg`,
		small_2x: `${DEMO_SLIDESHOW_PREFIX_2}/responsive/small_2x.jpeg`,
		small_1x: `${DEMO_SLIDESHOW_PREFIX_2}/responsive/small_1x.jpeg`,
	},
};

const DEMO_PHOTO_KEY_PREFIX =
	'https://uploads.mls.tours/twt/order/13670600-7146-4616-ae72-33cdf8e67485/job/ade0dca1-b291-4bef-ab70-be4062a0bd17';

export const DEMO_PHOTO_HASHES = [
	'b3731432a48d1e8e52ea143950b621d4',
	'df911348b955a2f32349ee75c76d0b96',
	'ae6b74c6fe88c5b9b1c2c52e495e3682',
	'72d2004a81b4e2aa03ba3e2bf0b0998e',
	'27b391ae6938decf8e8d4d2428ddc5e6',
	'87850f8d3d78fdf4a3db67cfeefd6e52',
	'f40295269583dd7e948e3ad384a2a3b9',
	'4ddd0a645ffe5a9fedb916f09fc993f7',
	'ffdb5e4890e2a3d741b1eeec0af0c25f',
	'6fb1b9dcbd17f458af70552420cf8313',
	'0ab5b7ecccd3bf61231b24a9ac8614a6',
	'92d578f7f18cad2ae0c91dd8f49531b4',
	'92129d6f4c3212596c719fa0e4580472',
	'3826a13a0ae9f9a64a944c062a95357b',
	'7471b206839d23f6ae64047cf01b37d7',
	'ecdacfe890b652d44a18cf9235a9fa48',
	'1320255be879c8291713844ee424286e',
	'f8370f884471dc343344c8fb844c66a1',
	'f34b9eb74580ee430dc33723e179e9cb',
	'03b155c7dc227106d909d96195ee4d75',
	'672e3a0a9ecd793cb9aaf2be8d8449a4',
	'3b102e621a332b2b544f715f9b1b5c90',
	'5f4fc0069d3a18c3466c0363f1d72fc7',
	'06bc5df9b2f6a7d9e8c4514f9927d534',
	'41b2b314847ef0203f62bb8a30566a20',
	'5bea144359e35310a15f4128ad315be5',
	'1d73832cfd8553dba523303a544ba441',
	'04564f3b05aff26aeaa77623ddb7cf6b',
	'dbe49e6ced31ad546117016624c67fca',
	'7c63cf303d475df02cb7522839ccbed8',
	'0e6e7f88f776887430811e8d22886262',
	'209534a6478e06c19f33601361a4b164',
	'3818086152add3982f2841890ad3eefd',
	'ecf3ef9b5cd2bbd0425ecc60f01cae33',
	'a5d4e9e5764fff6692b6b04330570cd5',
	'dde2b3fb80a9f3e87eadba4780dcddf8',
	'de32cd9eea13f86a328458251c007943',
	'a62dbe939ceedf9f2647cd202b57a26a',
	'417be65873649f10cf59b95d46914a41',
	'88278ac7a2c9399483af36dbf4e44cb9',
	'981f3181a9791236f9aa75ed300e113c',
	'0cdb4388a66fdd1f0f3c774a6d34abe5',
	'66b88e8bda3d143c013e15d7ee3ee497',
	'2e3bf103a833c622967fc2fcbffe715c',
	'ae2735d924edf233bdc407969d799d2c',
	'e1d82e89bd29476fabc61f07db4cf15b',
	'115c3413d232b4ffabacbf32b09b66b0',
	'0e358ff808e2399f354ee957c12aec64',
	'121b3bb46b784e58076b391ec1aa402f',
	'e4baf453bde151c31aad86bbc830c559',
];

export const DEMO_PHOTOS: MicrositeResponsiveImage[] = DEMO_PHOTO_HASHES.map((hash) => ({
	large_1x: `${DEMO_PHOTO_KEY_PREFIX}/${hash}/responsive/large_1x.jpeg`,
	large_2x: `${DEMO_PHOTO_KEY_PREFIX}/${hash}/responsive/large_2x.jpeg`,
	medium_1x: `${DEMO_PHOTO_KEY_PREFIX}/${hash}/responsive/medium_1x.jpeg`,
	medium_2x: `${DEMO_PHOTO_KEY_PREFIX}/${hash}/responsive/medium_2x.jpeg`,
	small_1x: `${DEMO_PHOTO_KEY_PREFIX}/${hash}/responsive/small_1x.jpeg`,
	small_2x: `${DEMO_PHOTO_KEY_PREFIX}/${hash}/responsive/small_2x.jpeg`,
	small_3x: `${DEMO_PHOTO_KEY_PREFIX}/${hash}/responsive/small_3x.jpeg`,
}));

export const DEMO_ADDRESS: MicrositeAddress = {
	line1: '2 Bank Street',
	line2: null,
	city: 'New London',
	state: 'CT',
	zip: '06320',
	long: -72.09381669999999,
	lat: 41.3539585,
};

export const DEMO_LISTING_DATA: MicrositeListingData = {
	mls: '1234567',
	beds: 4,
	baths: 3.5,
	built: 2003,
	lot_acres: 0.279,
	sqft: 3572,
	garage: 3,
	price_formatted: '10,523,000',
	body: `Nestled in the scenic hills of West Austin, this impressive home offers breathtaking panoramic views and generous space to live and grow. The main level features a spacious primary suite, dedicated home office, formal dining room, and an open-concept kitchen and living area ideal for everyday living and entertaining. A stunning wall of windows in the living area fills the space with natural light and frames the sweeping hill country views, while the primary bedroom offers its own serene outlook to start and end each day.

Upstairs includes secondary bedrooms, a versatile second living area, and a large media room, providing flexible space for work, play, and relaxation.

Enjoy outdoor living on the expansive deck overlooking the nearly 1/3 acre lot backing to a serene green space and rolling hills. With multiple areas for dining and gathering, this outdoor retreat offers privacy, peaceful views, and plenty of room to entertain or unwind.

Residents of the desirable Covered Bridge community enjoy access to a pavilion, pool and splash pad, park, playscape, dog park, and scenic walking trails. Construction is nearly complete, adding pickleball, tennis, and basketball courts. Walk to neighborhood coffee shop. Conveniently located near top-rated schools, upscale shopping, and a wide variety of dining options.

Schedule a showing today and discover the space, views, and lifestyle this West Austin home has to offer.`,
};
